
import { CookieConsentService } from '../../services/CookieConsentService.js'

export const CookieBanner = () => {
  // Check if already consented
  if (CookieConsentService.hasConsented()) return null

  const container = document.createElement('div')
  container.className = 'fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-fade-in-up'

  // Glassmorphism wrapper
  const wrapper = document.createElement('div')
  wrapper.className = 'max-w-6xl mx-auto bg-white/90 backdrop-blur-md border border-gray-200 shadow-2xl rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6'

  // Text content
  const content = document.createElement('div')
  content.className = 'flex-1'
  content.innerHTML = `
    <h3 class="text-lg font-bold text-gray-900 mb-2">We value your privacy</h3>
    <p class="text-sm text-gray-600 leading-relaxed">
      We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
      By clicking "Accept All", you consent to our use of cookies. 
      <button id="cookie-settings-link" class="text-primary-600 hover:text-primary-700 underline font-medium">Customize Settings</button>
    </p>
  `

  // Buttons
  const actions = document.createElement('div')
  actions.className = 'flex flex-col sm:flex-row gap-3 w-full md:w-auto'

  const denyBtn = document.createElement('button')
  denyBtn.className = 'px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors text-sm whitespace-nowrap'
  denyBtn.textContent = 'Reject All'

  const acceptBtn = document.createElement('button')
  acceptBtn.className = 'px-6 py-2.5 rounded-xl bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors text-sm shadow-lg whitespace-nowrap'
  acceptBtn.textContent = 'Accept All'

  actions.appendChild(denyBtn)
  actions.appendChild(acceptBtn)

  wrapper.appendChild(content)
  wrapper.appendChild(actions)
  container.appendChild(wrapper)

  // -- Event Handlers --

  const closeBanner = () => {
    container.classList.add('opacity-0', 'translate-y-10', 'transition-all', 'duration-300')
    setTimeout(() => container.remove(), 300)
  }

  acceptBtn.onclick = () => {
    CookieConsentService.setConsent({ analytics: true, marketing: true })
    closeBanner()
  }

  denyBtn.onclick = () => {
    CookieConsentService.setConsent({ analytics: false, marketing: false })
    closeBanner()
  }

  // -- Settings Modal Logic --
  const openSettings = (e) => {
    e.preventDefault()
    const modal = CookieSettingsModal(closeBanner)
    document.body.appendChild(modal)
  }

  const settingsLink = content.querySelector('#cookie-settings-link')
  if (settingsLink) settingsLink.onclick = openSettings

  return container
}

const CookieSettingsModal = (onSaveCallback) => {
  const overlay = document.createElement('div')
  overlay.className = 'fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in'

  const modal = document.createElement('div')
  modal.className = 'bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]'

  // Header
  const header = document.createElement('div')
  header.className = 'p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50'
  header.innerHTML = `
    <h2 class="text-xl font-bold text-gray-900">Cookie Preferences</h2>
    <button class="text-gray-400 hover:text-gray-600 transition-colors" id="close-modal-x">
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
    </button>
  `

  // Body
  const body = document.createElement('div')
  body.className = 'p-6 overflow-y-auto space-y-6'

  // Helper to create toggle section
  const createSection = (id, title, desc, required = false, checked = false) => {
    const div = document.createElement('div')
    div.className = 'flex items-start justify-between gap-4'
    div.innerHTML = `
      <div class="flex-1">
        <h4 class="text-base font-semibold text-gray-900 flex items-center gap-2">
          ${title}
          ${required ? '<span class="text-[10px] uppercase tracking-wider font-bold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full border border-primary-100">Required</span>' : ''}
        </h4>
        <p class="text-sm text-gray-500 mt-1">${desc}</p>
      </div>
      <label class="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
        <input type="checkbox" id="toggle-${id}" class="sr-only peer" ${checked ? 'checked' : ''} ${required ? 'disabled' : ''}>
        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 ${required ? 'opacity-60 cursor-not-allowed' : ''}"></div>
      </label>
    `
    return div
  }

  const currentConsent = CookieConsentService.getConsent() || { analytics: false, marketing: false }

  const essential = createSection('necessary', 'Strictly Necessary', 'These cookies are essential for the proper functioning of the website.', true, true)
  const analytics = createSection('analytics', 'Analytics & Performance', 'Help us understand how visitors interact with the website.', false, currentConsent.analytics)
  const marketing = createSection('marketing', 'Marketing', 'Used to deliver relevant advertisements and track ad performance.', false, currentConsent.marketing)

  body.appendChild(essential)
  body.appendChild(analytics)
  body.appendChild(marketing)

  // Footer
  const footer = document.createElement('div')
  footer.className = 'p-6 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-3'

  const cancelBtn = document.createElement('button')
  cancelBtn.className = 'px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-white transition-colors text-sm'
  cancelBtn.textContent = 'Cancel'

  const saveBtn = document.createElement('button')
  saveBtn.className = 'px-6 py-2.5 rounded-xl bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors text-sm shadow-lg'
  saveBtn.textContent = 'Save Preferences'

  footer.appendChild(cancelBtn)
  footer.appendChild(saveBtn)

  modal.appendChild(header)
  modal.appendChild(body)
  modal.appendChild(footer)
  overlay.appendChild(modal)

  // Logic
  const closeModal = () => overlay.remove()

  header.querySelector('#close-modal-x').onclick = closeModal
  cancelBtn.onclick = closeModal

  saveBtn.onclick = () => {
    const analyticsEnabled = body.querySelector('#toggle-analytics').checked
    const marketingEnabled = body.querySelector('#toggle-marketing').checked

    CookieConsentService.setConsent({
      analytics: analyticsEnabled,
      marketing: marketingEnabled
    })

    closeModal()
    if (onSaveCallback) onSaveCallback()
  }

  return overlay
}

export const openCookieSettings = () => {
  const modal = CookieSettingsModal(() => { })
  document.body.appendChild(modal)
}
